<script setup>

const { isSidebarVisible, isShareSheetOpen, isLiked, isDisliked, isSaved, currentArticle, localizedUrl, shareUrl, tagEvent, windowWidth, toggleModals, toggleSidebar, handleArticleAction, actionId, targetUrl, targetLabel, isGame } = defineProps({
	isSidebarVisible: Boolean,
	isShareSheetOpen: Boolean,
	isLiked: Boolean,
	isDisliked: Boolean,
	isSaved: Boolean,
	currentArticle: Object,
	localizedUrl: String,
	shareUrl: String,
	tagEvent: Function,
	windowWidth: Number,
	toggleModals: Function,
	toggleSidebar: Function,
	handleArticleAction: Function,
	actionId: String,
	targetUrl: String,
	targetLabel: { type: String, default: 'Open Article' },
	isGame: Boolean,
});

function handleAction(action) {
	handleArticleAction(action, undefined, actionId || currentArticle?.id);
}
function handleOpen() {
	if (!isGame) handleArticleAction('read', true, actionId || currentArticle?.id);
}

async function toggleShareSheet() {
	if (isShareSheetOpen) {
		toggleModals();
	} else if (isMobileDevice() && typeof navigator.share === 'function') {
		try {
			await navigator.share({ url: shareUrl || localizedUrl });
			tagEvent?.({ where: 'share_article', what: 'native_share' });
		} catch (error) {
			if (error?.name !== 'AbortError') toggleModals('share-sheet');
		}
	} else {
		toggleModals("share-sheet");
	}
}

function isMobileDevice() {
	return navigator.userAgentData?.mobile
		|| /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
		|| (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
</script>

<template>
<div id="article-reactions">
	<div class="button-bar">
		<a
			id="like"
			:class="['btn toggle', { active: isLiked }]"
			:title="isLiked ? 'Liked' : 'Like'"
			@click.stop="handleAction('like')"
		>
			<i class="fa-thumbs-up fa-regular"></i>
			<span class="hidden">Like</span>
		</a>

		<a
			id="dislike"
			:class="['btn toggle', { active: isDisliked }]"
			:title="isDisliked ? 'Disliked' : 'Dislike'"
			@click.stop="handleAction('dislike')"
		>
			<i class="fa-thumbs-down fa-regular"></i>
		</a>
	</div>

	<a
		id="saved"
		:class="['btn toggle', { active: isSaved }]"
		:title="isSaved ? 'Saved' : 'Save'"
		@click.stop="handleAction('saved')"
	>
		<i class="fa-bookmark fa-regular"></i>
		<!-- <span>{{isSaved ? 'Saved' : 'Save'}}</span> -->
	</a>
	<a
		id="share"
		:class="['btn toggle', { active: isShareSheetOpen }]"
		@click="toggleShareSheet()"
	>
		<span>Share</span>
	</a>
	<a
		id="open"
		v-if="windowWidth < 1024 && windowWidth >= 768"
			:href="targetUrl || localizedUrl"
		class="btn open-article primary"
		target="_blank"
			@click.stop="handleOpen()"
		>
			<span>{{ targetLabel }}</span>
	</a>
</div>
</template>

<style lang="scss" scoped>
#article-reactions {
	--distanceFromLeft: 16px;
	--distanceFromRight: 14px;
	background: var(--bg3);
	background-color: var(--bg3a9);
	backdrop-filter: blur(3px) brightness(1.2);
	border: 1px solid var(--border);
	border-radius: 18px; /* match global-nav */
	box-shadow: var(--floatingShadow);
	padding: 4px;

	display: flex;
	justify-content: stretch;
	gap: 4px;

	position: fixed;
	left: calc(100vw + var(--distanceFromLeft));
	right: calc(200vw - var(--distanceFromRight));
	transition: left 300ms ease, right 300ms ease;
	bottom: 64px;
	margin: 0 -6px 0;
	width: calc(var(--main-content-width) - var(--distanceFromLeft) - var(--distanceFromRight));
	z-index: 5;
}
#sidebar.collapsed + #article-overview #article-reactions {
	left: var(--distanceFromLeft);
	right: var(--distanceFromRight);
}
#sidebar.collapsed + #games-overview #article-reactions {
	left: var(--distanceFromLeft);
	right: var(--distanceFromRight);
}
#article-reactions > * {
	flex-grow: 2;
	user-select: none;
	-webkit-user-select: none;
}
.open-article {
	border: 1px solid var(--border);
}
@media (min-width: 768px) {
	#sidebar.collapsed + #article-overview #article-reactions,
	#article-reactions {
		position: fixed;
		margin: 0;
		inset: auto 10px 8px auto;
		width: calc(
			var(--main-content-width) - (10px + 5px + 1px) * 2 - 1px
		); // 10px inset, 5px padding, 1px border on each side
		transition: unset;
	}
}
@media (min-width: 1024px) {
	/* matches when 3rd column appears*/
	#sidebar.collapsed + #article-overview #article-reactions,
	#article-reactions {
		background: unset;
		backdrop-filter: unset;
		border: unset;
		border-radius: unset;
		box-shadow: unset;
		padding: unset;
		position: static !important;
		inset: unset !important;
		width: unset !important;
	}
	#article-reactions > * {
		font-size: 1rem;
	}
	.button-bar .btn.toggle {
		padding: 6px 12px;
	}
}
</style>
